import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';

/**
 * Formatting shared by the colleague list card and the map popup.
 *
 * The two render the same ticket in two places, so anything that decides *wording* lives here
 * rather than in either view — otherwise the popup slowly drifts away from the card it is
 * supposed to mirror.
 */

/**
 * Turn a scheduling window into the clock-icon label.
 *
 * ServiceNow has no "all day" flag — the window itself is the signal. On the live instance every
 * full-day job comes through as 07:00–16:00, so a window that spans at least that is shown as
 * "All day" and anything narrower as its own `HH-HH` slot. A ticket with no window at all reads
 * "Not set", matching the legacy tool.
 */
export function windowLabel(windowStart: string | null | undefined, windowEnd: string | null | undefined): string {
  if (!windowStart || !windowEnd) return m.colleague_time_not_set();

  const startHour = Number(windowStart.slice(11, 13));
  const endHour = Number(windowEnd.slice(11, 13));
  if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return m.colleague_time_not_set();

  if (startHour <= 7 && endHour >= 16) return m.colleague_all_day();
  return `${startHour}-${endHour}`;
}

/** Who the mail button addresses — the resolved service-desk setting (see api/service-desk-queries.ts). */
export interface MailRecipients {
  to?: string | null;
  cc?: readonly string[] | null;
}

// Addresses are validated server-side, but `?`, `&`, `#` and `%` are legal in a local part and would break
// the URL, so every address is percent-encoded (keeping the `@` readable, which mail clients prefer).
const encodeAddress = (address: string): string => encodeURIComponent(address).replace(/%40/g, '@');

/**
 * `mailto:` for one ticket — subject and body prefilled, recipients from the service-desk setting.
 * With no recipient set the link is still valid: `mailto:?subject=…` opens the mail program with the subject and
 * body filled and the recipients left blank.
 */
export function mailtoHref(ticket: ServiceNowTicket, recipients: MailRecipients | null | undefined): string {
  const number = ticket.number ?? '';
  // The client's own reference (Dell dispatch number) follows the WOT number, so the desk can match the
  // mail to either system. Omitted when ServiceNow has none.
  const subject = [`Update for ${number}`, ticket.correlationId].filter(Boolean).join(' ');
  const body = `Hello NSC Service Desk,\n\n${number}:\n`;
  const cc = (recipients?.cc ?? []).filter(Boolean).map(encodeAddress).join(',');
  const query = [cc ? `cc=${cc}` : '', `subject=${encodeURIComponent(subject)}`, `body=${encodeURIComponent(body)}`]
    .filter(Boolean)
    .join('&');
  return `mailto:${recipients?.to ? encodeAddress(recipients.to) : ''}?${query}`;
}

/** `yyyy-mm-dd hh:mm:ss` window start → `dd/mm/yyyy`; empty when there is no window. */
export function windowDate(windowStart: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(windowStart ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** `https://www.google.com/maps/search/?api=1&query=<lat>,<lng>` — same target as the list's coordinate link. */
export function googleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}
